/**
 * SuiteFormDialog · Foundation Component (candidato BR-FND-002)
 *
 * Standard Foundation per i dialog operativi della GI Suite.
 * Caratteristiche:
 *   - larghezza coerente con il Design System (max-w-3xl di default);
 *   - altezza massima ~85vh con scroll INTERNO del contenuto;
 *   - header sticky in alto;
 *   - footer sticky in basso con pulsanti Annulla/Salva SEMPRE visibili;
 *   - gap verticale uniforme fra i gruppi di campi (gap-6);
 *   - integrazione nativa con SuiteTabs (nessun collasso di width);
 *   - zero logica di dominio: componente puro.
 *
 * SafeFormDialog resta disponibile per retrocompatibilità con i
 * consumer esistenti e verrà migrato progressivamente.
 */
import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const SIZE = {
  sm: "max-w-md",
  md: "max-w-xl",
  lg: "max-w-3xl",   // default per form operativi
  xl: "max-w-5xl",
};

export function SuiteFormDialog({
  open,
  onOpenChange,
  title,
  description,
  size = "lg",
  onSubmit,                      // (e) => Promise<void> | void
  onCancel,
  submitLabel = "Salva",
  cancelLabel = "Annulla",
  submitting = false,
  canSubmit = true,
  hideSubmit = false,            // v1.3.4+: nasconde il pulsante Salva (es. ruoli in sola lettura)
  isDirty = false,               // v1.3.4+: se true, conferma prima di chiudere con modifiche non salvate
  preventOutsideClose = true,    // v1.3.4+: click sul backdrop + ESC non chiudono (safe form standard)
  contentClassName,
  bodyClassName,
  footerExtras,                  // slot per azioni ausiliarie (es. "Duplica")
  testid = "suite-form-dialog",
  children,
}) {
  const [confirmUnsaved, setConfirmUnsaved] = React.useState(false);
  React.useEffect(() => { if (!open) setConfirmUnsaved(false); }, [open]);

  const attemptClose = React.useCallback(() => {
    if (isDirty) {
      setConfirmUnsaved(true);
      return;
    }
    onOpenChange?.(false);
  }, [isDirty, onOpenChange]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!submitting && canSubmit && onSubmit) onSubmit(e);
  };
  const handleCancel = () => {
    onCancel?.();
    attemptClose();
  };
  const forceClose = () => {
    setConfirmUnsaved(false);
    onOpenChange?.(false);
  };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={(next) => {
      if (next === false) { attemptClose(); return; }
      onOpenChange?.(next);
    }}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className="fixed inset-0 z-50 bg-black/60 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        />
        <DialogPrimitive.Content
          data-testid={testid}
          className={cn(
            "fixed left-[50%] top-[50%] z-50 flex flex-col w-[calc(100vw-2rem)] min-w-0",
            SIZE[size] || SIZE.lg,
            "translate-x-[-50%] translate-y-[-50%] border bg-white text-slate-900 shadow-2xl duration-200",
            "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 sm:rounded-lg",
            "max-h-[85vh] overflow-hidden",
            contentClassName,
          )}
          onPointerDownOutside={preventOutsideClose ? (e) => e.preventDefault() : undefined}
          onInteractOutside={preventOutsideClose ? (e) => e.preventDefault() : undefined}
          onEscapeKeyDown={preventOutsideClose ? (e) => e.preventDefault() : undefined}
        >
          <form onSubmit={handleSubmit} className="flex flex-col h-full min-h-0">
            {/* HEADER sticky */}
            <header className="shrink-0 px-6 py-4 border-b bg-white">
              <DialogPrimitive.Title className="text-lg font-semibold leading-tight">
                {title}
              </DialogPrimitive.Title>
              {description && (
                <DialogPrimitive.Description className="text-sm text-slate-500 mt-1">
                  {description}
                </DialogPrimitive.Description>
              )}
              <DialogPrimitive.Close
                onClick={handleCancel}
                className="absolute right-4 top-4 rounded-md opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring"
                aria-label="Chiudi"
              >
                <X className="h-5 w-5" />
              </DialogPrimitive.Close>
            </header>

            {/* BODY scrollabile */}
            <div
              className={cn(
                "flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-6 py-5",
                "flex flex-col gap-6",
                bodyClassName,
              )}
              data-testid={`${testid}-body`}
            >
              {children}
            </div>

            {/* FOOTER sticky */}
            <footer className="shrink-0 px-6 py-3 border-t bg-white flex items-center justify-end gap-2">
              {footerExtras}
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={submitting}
                data-testid={`${testid}-cancel`}
              >
                {cancelLabel}
              </Button>
              {!hideSubmit && (
                <Button
                  type="submit"
                  disabled={submitting || !canSubmit}
                  data-testid={`${testid}-submit`}
                >
                  {submitting ? "Salvataggio…" : submitLabel}
                </Button>
              )}
            </footer>
          </form>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>

      {/* Conferma modifiche non salvate (BR-FND-002 candidate · parità funzionale con SafeFormDialog) */}
      <DialogPrimitive.Root open={confirmUnsaved} onOpenChange={setConfirmUnsaved}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className="fixed inset-0 z-[60] bg-black/60" />
          <DialogPrimitive.Content
            role="alertdialog"
            aria-modal="true"
            className="fixed left-[50%] top-[50%] z-[70] grid w-full max-w-md translate-x-[-50%] translate-y-[-50%] gap-4 border bg-white p-6 shadow-2xl sm:rounded-lg"
            onPointerDownOutside={(e) => e.preventDefault()}
            onInteractOutside={(e) => e.preventDefault()}
            data-testid={`${testid}-unsaved-changes`}
          >
            <div>
              <DialogPrimitive.Title className="text-lg font-semibold text-slate-900">
                Modifiche non salvate
              </DialogPrimitive.Title>
              <DialogPrimitive.Description className="text-sm text-slate-600 mt-2 leading-relaxed">
                Sono presenti modifiche non salvate.<br />
                Se esci ora tutte le modifiche inserite verranno perse.<br />
                <strong>Vuoi continuare?</strong>
              </DialogPrimitive.Description>
            </div>
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={forceClose}
                      data-testid={`${testid}-unsaved-discard`}>
                Continua senza salvare
              </Button>
              <Button type="button" autoFocus onClick={() => setConfirmUnsaved(false)}
                      className="bg-emerald-800 hover:bg-emerald-700 text-white"
                      data-testid={`${testid}-unsaved-back`}>
                Torna alla modifica
              </Button>
            </div>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    </DialogPrimitive.Root>
  );
}

/**
 * Sezione del form con titolo opzionale e spaziatura standard.
 * Usabile come figlio diretto di <SuiteFormDialog>.
 */
export function SuiteFormSection({ title, description, children, className, testid }) {
  return (
    <section className={cn("flex flex-col gap-3", className)} data-testid={testid}>
      {title && (
        <div className="flex flex-col gap-0.5">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-700">{title}</h3>
          {description && <p className="text-xs text-slate-500">{description}</p>}
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>
    </section>
  );
}
